<template>
  <div>
    <b-container fluid class="pb-4 px-lg-4">
      <!-- ── SKELETON ── -->
      <div v-if="!apiResponse.success" class="cat-skeleton">
        <div class="cat-sk-toolbar">
          <div class="cat-sk-bar cat-sk-search"></div>
          <div class="cat-sk-bar cat-sk-select"></div>
          <div class="cat-sk-bar cat-sk-btn"></div>
        </div>
        <div class="cat-sk-grid">
          <div v-for="n in 8" :key="n" class="cat-sk-card">
            <div class="cat-sk-card-header">
              <div class="cat-sk-circle"></div>
              <div class="cat-sk-lines">
                <div class="cat-sk-bar cat-sk-line-title"></div>
                <div class="cat-sk-bar cat-sk-line-sub"></div>
              </div>
            </div>
            <div class="cat-sk-divider"></div>
            <div class="cat-sk-card-footer">
              <div class="cat-sk-bar cat-sk-price"></div>
              <div class="cat-sk-bar cat-sk-toggle"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <!-- Toolbar unificada -->
        <div class="dir-toolbar">
          <div class="dir-toolbar-search">
            <el-input
              v-model="searchQuery"
              placeholder="Buscar producto, código..."
              suffix-icon="el-icon-search"
              size="small"
              clearable
              @input="onSearchDebounced"
            />
          </div>
          <el-select
            v-model="orderByQuery"
            placeholder="Ordenar"
            size="small"
            class="dir-toolbar-select"
          >
            <el-option label="Recientes" value="recent" />
            <el-option label="Nombre A-Z" value="a-z" />
            <el-option label="Nombre Z-A" value="z-a" />
            <el-option label="Mayor valor" value="top-price" />
          </el-select>
          <div class="dir-toolbar-right">
            <button
              v-if="productSelect.length"
              class="cat-action-btn cat-action-btn--danger"
              @click="productsDeleteOuterVisible = true"
              title="Eliminar seleccionados"
            >
              <i class="el-icon-delete"></i>
            </button>
            <ExportButtons
              :data="sortedProducts"
              :columns="exportColumns"
              filename="listado_productos"
            />
            <button
              class="dir-view-toggle"
              :class="{ active: viewMode === 'grid' }"
              @click="viewMode = 'grid'"
            >
              <i class="el-icon-menu"></i>
            </button>
            <button
              class="dir-view-toggle"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <i class="el-icon-s-unfold"></i>
            </button>
          </div>
        </div>

        <!-- GRID VIEW (cards) -->
        <div v-if="viewMode === 'grid'" class="dir-grid">
          <div
            v-for="product in sortedProducts"
            :key="product.id"
            class="dir-card"
            @click="goRouter('catalogue.products.edit', { id: product.id })"
          >
            <div class="dir-card-header">
              <ImagePreviewCircleComponent
                :urlImage="product.image"
                style="flex-shrink: 0;"
              />
              <div style="flex: 1; min-width: 0;">
                <span class="dir-card-name">{{ product.name }}</span>
                <span class="cat-card-code"
                  >{{ product.code
                  }}<template v-if="product.alternative_code">
                    ·
                    <span style="opacity:0.7;">{{
                      product.alternative_code
                    }}</span></template
                  ></span
                >
              </div>
            </div>
            <div v-if="getCategories(product)" class="dir-card-meta">
              <i class="el-icon-collection-tag"></i>{{ getCategories(product) }}
            </div>
            <div class="dir-card-footer">
              <span class="dir-card-amount">${{ product.amount }}</span>
              <span class="cat-card-status" @click.stop>
                <SwitchParametro
                  :setValue.sync="product.status_product"
                  :setStatusId="product.id"
                  @onChange="onChange"
                />
              </span>
              <span
                v-if="product.is_stock_counter"
                class="cat-card-stock"
                @click.stop="showModal(product.stocks)"
              >
                <i class="el-icon-box"></i> Inventario
              </span>
            </div>
          </div>
        </div>

        <!-- LIST VIEW (tabla): solo visible cuando hay productos -->
        <div v-else-if="sortedProducts.length" class="dir-table">
          <div class="dir-table-header cat-table-header">
            <span style="width: 32px;">
              <b-form-checkbox
                v-model="checkAll"
                size="lg"
                class="custom-checkbox"
                @change="handleCheckAllChange"
              />
            </span>
            <span>Código</span>
            <span class="cat-col-img-header">Imagen</span>
            <span class="cat-col-name">Nombre</span>
            <span>Estado</span>
            <span>Categoría</span>
            <span>Valor</span>
            <span style="width: 60px;">Stock</span>
          </div>
          <div
            v-for="product in sortedProducts"
            :key="'list-' + product.id"
            class="dir-table-row cat-table-row"
          >
            <span style="width: 32px;" @click.stop>
              <b-form-checkbox
                v-model="productSelect"
                :value="product.id"
                size="lg"
              />
            </span>
            <span
              class="cat-code-cell"
              @click="goRouter('catalogue.products.edit', { id: product.id })"
            >
              <span class="cat-code-text">{{ product.code }}</span>
              <span v-if="product.alternative_code" class="cat-alt-code-text">{{
                product.alternative_code
              }}</span>
            </span>
            <span
              class="cat-col-thumb"
              @click="goRouter('catalogue.products.edit', { id: product.id })"
            >
              <div class="cat-thumb">
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.name"
                  class="cat-thumb-img"
                />
                <i v-else class="el-icon-box cat-thumb-placeholder"></i>
              </div>
            </span>
            <span
              class="cat-col-name cat-name-text"
              @click="goRouter('catalogue.products.edit', { id: product.id })"
            >
              {{ product.name }}
            </span>
            <span @click.stop>
              <SwitchParametro
                :setValue.sync="product.status_product"
                :setStatusId="product.id"
                @onChange="onChange"
              />
            </span>
            <span class="cat-category-text">{{ getCategories(product) }}</span>
            <span class="cat-amount-text">${{ product.amount }}</span>
            <span style="width: 60px;">
              <el-button
                v-if="product.is_stock_counter"
                size="mini"
                type="primary"
                icon="el-icon-search"
                @click.stop="showModal(product.stocks)"
              />
            </span>
          </div>
          <div v-if="!sortedProducts.length" class="dir-table-empty">
            Sin resultados
          </div>
        </div>
      </div>
      <!-- end v-else -->

      <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler">
        <div slot="no-more" class="dir-table-empty">No hay más productos</div>
        <div slot="no-results">
          <EmptyPage
            v-if="apiResponse.success"
            icon="el-icon-box"
            text="Registra tus productos o servicios y empieza a ofrecer a tus clientes."
          />
        </div>
        <div slot="spinner"></div>
      </infinite-loading>
    </b-container>

    <WrapperCatalogTourComponent />

    <!-- Dialog: Confirmar eliminación -->
    <el-dialog width="400px" :visible.sync="productsDeleteOuterVisible">
      <div class="wrapper__invoice-delete text-center">
        <img :src="icoNotifyDanger" alt="ico danger" width="30" />
        <h4
          class="h4 font-semi-bold text-break text-color-primary mt-4"
          v-if="productSelect.length > 0"
        >
          ¿Seguro desea eliminar estos registros?
        </h4>
        <h4 class="h4 font-semi-bold text-break text-color-primary mt-4" v-else>
          Debe seleccionar al menos un producto
        </h4>
        <div class="d-flex justify-content-center align-items-center mt-4">
          <button @click="confirmDelete" class="btn md__btn-secondary mr-4">
            Cancelar
          </button>
          <!-- Sin v-if: el diálogo solo se abre desde el botón de la barra
               de acciones, que ya está gateado por productSelect.length
               (línea ~55) — un guard redundante aquí dejaba el botón
               "Eliminar" sin renderizar en producción. -->
          <ButtonPrimaryComponent
            title="Eliminar"
            @onSaveClick="handleProductDelete"
          />
        </div>
      </div>
    </el-dialog>

    <!-- Dialog: Inventario -->
    <el-dialog
      :visible.sync="dialogVisible"
      width="480px"
      :show-close="false"
      custom-class="inventory-dialog"
    >
      <div class="inv-dialog-header">
        <div class="inv-dialog-header-left">
          <i class="el-icon-box inv-dialog-icon"></i>
          <h5 class="inv-dialog-title">Inventario</h5>
        </div>
        <button class="inv-dialog-close" @click="dialogVisible = false">
          <i class="el-icon-close"></i>
        </button>
      </div>
      <div class="inv-dialog-body">
        <div v-for="(stock, idx) in stocks" :key="idx" class="inv-stock-row">
          <div class="inv-stock-info">
            <i class="el-icon-office-building inv-stock-icon"></i>
            <span class="inv-stock-name">{{ stock.establishment }}</span>
          </div>
          <span
            :class="[
              'inv-stock-qty',
              stock.quantity > 0
                ? 'qty-ok'
                : stock.quantity < 0
                ? 'qty-danger'
                : 'qty-zero'
            ]"
          >
            {{ stock.quantity }} uds.
          </span>
        </div>
        <div v-if="!stocks.length" class="inv-stock-empty">
          Sin datos de inventario
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ImagePreviewCircleComponent from "../../../components/ImagePreviewCircle";
import catalogueService from "../../services/catalogueService";
import SwitchParametro from "./SwitchParametros";
import WrapperCatalogTourComponent from "../WrapperTour";
import EmptyPage from "../../../components/EmptyPage";
import { icoNotifyDanger, icoTrash } from "@/services/resources";

export default {
  name: "ProductListComponent",
  data: () => ({
    icoNotifyDanger,
    icoTrash,
    activetrash: false,
    filters: { search: null, page: 1 },
    infiniteId: +new Date(),
    hasMore: true,
    eraserPublic: { id: "", status_product: false },
    apiResponse: { success: false },
    searchQuery: "",
    orderByQuery: "recent",
    viewMode: "list",
    items: [],
    dialogVisible: false,
    stocks: [],
    checkAll: false,
    productSelect: [],
    productsDeleteOuterVisible: false,
    _searchTimer: null
  }),
  computed: {
    sortedProducts() {
      return this.items.concat().sort((a, b) => {
        switch (this.orderByQuery) {
          case "recent":
            return (b.id || 0) - (a.id || 0);
          case "old":
            return (a.id || 0) - (b.id || 0);
          case "a-z":
            return (a.name || "")
              .toLowerCase()
              .localeCompare((b.name || "").toLowerCase());
          case "z-a":
            return (b.name || "")
              .toLowerCase()
              .localeCompare((a.name || "").toLowerCase());
          case "top-price":
            return (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
          default:
            return 0;
        }
      });
    },
    exportColumns() {
      return [
        { label: "Código", field: "code" },
        { label: "SKU Alternativo", field: "alternative_code" },
        { label: "Nombre", field: "name" },
        { label: "Descripción", field: "description" },
        { label: "Precio", field: "amount" },
        {
          label: "Tipo",
          value: i => (i.type === "service" ? "Servicio" : "Producto")
        },
        {
          label: "Estado",
          value: i => (i.status_product ? "Activo" : "Inactivo")
        },
        {
          label: "Control inventario",
          value: i => (i.is_stock_counter ? "Sí" : "No")
        },
        {
          label: "Stock total",
          value: i =>
            i.stocks
              ? i.stocks.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0)
              : ""
        },
        {
          label: "Stock por establecimiento",
          value: i =>
            i.stocks && i.stocks.length
              ? i.stocks
                  .map(s => `${s.establishment}: ${s.quantity}`)
                  .join(" | ")
              : ""
        }
      ];
    }
  },
  watch: {
    productSelect() {
      this.checkAll = this.productSelect.length === this.sortedProducts.length;
      this.activetrash = this.productSelect.length > 0;
    },
    viewMode(val) {
      this.$store.dispatch("setViewPreference", {
        key: "products",
        value: val
      });
    }
  },
  methods: {
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    onSearchDebounced() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.search = this.searchQuery || null;
        this.filters.page = 1;
        this.items = [];
        this.hasMore = true;
        this.infiniteId += 1;
      }, 400);
    },
    handleCheckAllChange() {
      if (this.checkAll) {
        this.productSelect = this.sortedProducts.map(p => p.id);
      } else {
        this.productSelect = [];
      }
    },
    confirmDelete() {
      this.productsDeleteOuterVisible = !this.productsDeleteOuterVisible;
    },
    async onChange({ value, codigo }) {
      try {
        this.eraserPublic.id = codigo;
        this.eraserPublic.status_product = value;
        let response = await catalogueService.getEraserPublic(
          this.eraserPublic
        );
        const { success, message } = response.data;
        if (success) {
          this.$notifications.success({ message });
        }
      } catch (error) {
        return false;
      }
    },
    getCategories({ user_business_category = [] }) {
      return user_business_category.map(c => c.title).join(", ");
    },
    async infiniteHandler($state) {
      if (!this.hasMore) {
        $state.complete();
        return;
      }
      const { page, search } = this.filters;
      try {
        let response = await catalogueService.getProducts({
          params: { page, search, limit: 10 }
        });
        const { products, pagination } = response?.data;
        if (products && products.length) {
          this.items = [...this.items, ...products];
          this.filters.page += 1;
          this.apiResponse.success = true;
          this.$emit(
            "update-product-count",
            pagination?.total || this.items.length
          );
          if (!pagination || page >= pagination.total_pages) {
            this.hasMore = false;
            $state.complete();
          } else {
            $state.loaded();
          }
        } else {
          this.apiResponse.success = true;
          this.hasMore = false;
          $state.complete();
        }
      } catch (error) {
        $state.complete();
      }
    },
    async handleProductDelete() {
      this.$store.dispatch("toggleRequestLoading");
      try {
        let response = await catalogueService
          .deleteProductsIds({ ids: this.productSelect })
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
            this.confirmDelete();
          });
        const { success, message } = response.data;
        if (success) {
          this.$notifications.success({ message });
        }
        // Reset and reload
        this.items = [];
        this.filters.page = 1;
        this.hasMore = true;
        this.infiniteId += 1;
      } catch (error) {
        return false;
      }
    },
    showModal(data) {
      this.stocks = data;
      this.dialogVisible = true;
    }
  },
  components: {
    ImagePreviewCircleComponent,
    WrapperCatalogTourComponent,
    EmptyPage,
    SwitchParametro
  },
  mounted() {
    const saved = this.$store.getters.getViewPreference("products");
    if (saved) this.viewMode = saved;
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$border: #e4eaf6;
$bg: #f7f8f9;

.dir-toolbar .el-input__inner,
.dir-toolbar .el-select .el-input__inner {
  min-height: 34px !important;
  height: 34px;
  font-size: 13px;
  border-radius: 8px;
}

// Inventory dialog
.inv-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid $border;
  margin: -20px -20px 0;
}
.inv-dialog-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.inv-dialog-icon {
  font-size: 18px;
  color: #534ab7;
}
.inv-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.inv-dialog-close {
  width: 36px;
  height: 36px;
  border: none;
  background: $primary;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.85;
  }
}
.inv-dialog-body {
  padding: 16px 0 0;
}
.inv-stock-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 4px;
  border-bottom: 1px solid $border;
  &:last-child {
    border-bottom: none;
  }
}
.inv-stock-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.inv-stock-icon {
  font-size: 16px;
  color: #909399;
}
.inv-stock-name {
  font-size: 14px;
  font-weight: 500;
  color: $primary;
}
.inv-stock-qty {
  font-size: 15px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 8px;
  &.qty-ok {
    background: #e4fff4;
    color: #1d9e75;
  }
  &.qty-zero {
    background: $bg;
    color: #909399;
  }
  &.qty-danger {
    background: #fff0f0;
    color: #e24b4a;
  }
}
.inv-stock-empty {
  text-align: center;
  padding: 32px 0;
  color: #c0c4cc;
  font-size: 14px;
}

// Card extras
.cat-card-code {
  font-size: 11px;
  color: #909399;
  display: block;
}
.cat-card-status {
  flex-shrink: 0;
}
.cat-card-stock {
  font-size: 11px;
  color: #534ab7;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.cat-action-btn {
  width: 34px;
  height: 34px;
  border: 1px solid $border;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #909399;
  transition: all 0.15s;
  &--danger:hover {
    background: #fff0f0;
    border-color: #e24b4a;
    color: #e24b4a;
  }
}
</style>

<!-- Non-scoped for table grid overrides + dialog styling -->
<style lang="scss">
.inventory-dialog {
  border-radius: 12px;
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 20px;
  }
}

.cat-table-header,
.cat-table-row {
  grid-template-columns: 32px 80px 48px 1fr 80px 140px 90px 60px !important;
}
.cat-code-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  min-width: 0;
}
.cat-code-text {
  font-weight: 600;
  color: #31355c;
  font-size: 14px;
  line-height: 1.3;
}
.cat-alt-code-text {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cat-col-name {
  min-width: 0;
}
.cat-name-text {
  font-weight: 600;
  color: #31355c;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cat-category-text {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cat-amount-text {
  font-weight: 600;
  font-size: 14px;
  color: #31355c;
}
.cat-col-img-header {
  font-size: 12px;
}
.cat-col-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.cat-thumb {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: #f4f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ecf1;
}
.cat-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cat-thumb-placeholder {
  font-size: 18px;
  color: #c0c4cc;
}

@media (max-width: 1024px) {
  .cat-table-header,
  .cat-table-row {
    grid-template-columns: 32px 70px 48px 1fr 80px 90px 60px !important;
  }
  .cat-table-header span:nth-child(6),
  .cat-table-row span:nth-child(6) {
    display: none !important;
  }
}
@media (max-width: 768px) {
  .cat-table-header {
    display: none !important;
  }
  .cat-table-row {
    display: flex !important;
    flex-wrap: wrap;
    gap: 6px 12px;
    padding: 16px;
    font-size: 14px;

    // Checkbox stays
    > span:first-child {
      flex-shrink: 0;
    }

    // Name takes full width
    .cat-name-text {
      flex-basis: 100%;
      order: -2;
      font-size: 15px;
    }
    // Code secondary
    .cat-code-text {
      font-size: 13px;
      color: #909399;
    }
    // Thumbnail beside name on mobile
    .cat-col-thumb {
      order: -3;
      flex-shrink: 0;
    }
    .cat-category-text {
      display: none !important;
    }
  }
}

/* ── Catalogue Skeleton ── */
@keyframes cat-shimmer {
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
}
.cat-sk-bar {
  border-radius: 6px;
  background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
  background-size: 800px 100%;
  animation: cat-shimmer 1.4s infinite linear;
}
.cat-sk-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
.cat-sk-search {
  height: 32px;
  flex: 1;
  max-width: 360px;
}
.cat-sk-select {
  height: 32px;
  width: 130px;
}
.cat-sk-btn {
  height: 32px;
  width: 80px;
  margin-left: auto;
}
.cat-sk-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.cat-sk-card {
  border: 1px solid #e4eaf6;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
}
.cat-sk-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.cat-sk-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
  background-size: 800px 100%;
  animation: cat-shimmer 1.4s infinite linear;
}
.cat-sk-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cat-sk-line-title {
  height: 14px;
  width: 70%;
}
.cat-sk-line-sub {
  height: 11px;
  width: 45%;
}
.cat-sk-divider {
  height: 1px;
  background: #e4eaf6;
  margin: 10px 0;
}
.cat-sk-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cat-sk-price {
  height: 16px;
  width: 60px;
}
.cat-sk-toggle {
  height: 22px;
  width: 44px;
  border-radius: 11px;
}
@media (max-width: 768px) {
  .cat-sk-grid {
    grid-template-columns: 1fr;
  }
}
</style>
